import type { Epic } from 'redux-observable';
import { ofType } from 'redux-observable';
import { mergeMap, catchError } from 'rxjs/operators';
import { of, forkJoin } from 'rxjs';

import { NotificationService } from 'ui-components/services/notification-service';

import * as Actions from './freight-settings.actions';
import * as FreightSettingsApi from './freight-settings.api';
import type { FreightProvider } from './freight-settings.types';

const providerCodeToApiCode: Record<string, string> = {
  SPEEDSHIP: 'wwex_freight',
};

export const KNOWN_PROVIDERS: FreightProvider[] = [
  {
    freightProviderId: 1,
    sellerFreightProviderId: null,
    code: 'SPEEDSHIP',
    name: 'SpeedShip',
    apiCode: 'wwex_freight',
    isConnected: false,
  },
];

export type ProviderApiDto = {
  freightProviderId: number;
  providerCode?: string;
  code?: string;
  providerName?: string;
  name?: string;
};

export type SellerProviderApiDto = {
  sellerFreightProviderId: number;
  freightProviderId: number;
  providerCode?: string;
  connectedAt?: string;
};

export const mergeWithKnownProviders = (apiProviders: FreightProvider[]): FreightProvider[] => {
  if (apiProviders.length > 0) {
    return apiProviders;
  }
  return KNOWN_PROVIDERS;
};

export const combineProvidersAndSellerProviders = (
  providers: ProviderApiDto[],
  sellerProviders: SellerProviderApiDto[],
): FreightProvider[] => {
  const sellerProviderByFreightProviderId = sellerProviders.reduce<Record<number, SellerProviderApiDto>>(
    (acc, sp) => ({ ...acc, [sp.freightProviderId]: sp }),
    {},
  );

  return providers.map((p) => {
    const providerCode = p.providerCode ?? p.code ?? '';
    const providerName = p.providerName ?? p.name ?? '';
    const matchingSellerProvider = sellerProviderByFreightProviderId[p.freightProviderId];
    return {
      freightProviderId: p.freightProviderId,
      sellerFreightProviderId: matchingSellerProvider?.sellerFreightProviderId ?? null,
      code: providerCode,
      name: providerName,
      apiCode: providerCodeToApiCode[providerCode] ?? providerCode.toLowerCase(),
      isConnected: Boolean(matchingSellerProvider),
      connectedAt: matchingSellerProvider?.connectedAt,
    };
  });
};

export const getProvidersEpic: Epic = (action$) => {
  return action$.pipe(
    ofType(Actions.FREIGHT_GET_PROVIDERS_REQUEST),
    mergeMap(() => {
      return forkJoin({
        providersResponse: FreightSettingsApi.getProviders(),
        sellerProvidersResponse: FreightSettingsApi.getSellerProviders(),
      }).pipe(
        mergeMap(({ providersResponse, sellerProvidersResponse }) => {
          // The providers endpoint may return either an array directly or an object with a `providers` key.
          const providersArray: ProviderApiDto[] = Array.isArray(providersResponse)
            ? providersResponse
            : (providersResponse?.providers ?? []);
          // The seller-provider endpoint may return either an array directly or an object with a `sellerFreightProviders` key.
          const sellerProvidersArray: SellerProviderApiDto[] = Array.isArray(sellerProvidersResponse)
            ? sellerProvidersResponse
            : (sellerProvidersResponse?.sellerFreightProviders ?? []);

          const providers = combineProvidersAndSellerProviders(providersArray, sellerProvidersArray);
          return of(Actions.getProvidersSuccess(mergeWithKnownProviders(providers)));
        }),
        catchError(() => {
          return of(Actions.getProvidersSuccess(KNOWN_PROVIDERS));
        }),
      );
    }),
  );
};

export const disconnectProviderEpic: Epic = (action$) => {
  return action$.pipe(
    ofType(Actions.FREIGHT_DISCONNECT_PROVIDER_REQUEST),
    mergeMap((action: { sellerFreightProviderId: number }) => {
      const { sellerFreightProviderId } = action;

      return FreightSettingsApi.disconnectProvider(sellerFreightProviderId).pipe(
        mergeMap(() => {
          NotificationService.showGenericSuccessNotification('Freight provider disconnected.');
          return of(Actions.disconnectProviderSuccess(), Actions.getProvidersRequest());
        }),
        catchError((err) => {
          const errorMessage = err?.response?.data?.message ?? 'Failed to disconnect freight provider.';
          return of(Actions.disconnectProviderError(errorMessage));
        }),
      );
    }),
  );
};
